import React from "react";
import { 
  Grid, 
  Card, 
  CardContent, 
  Typography, 
  Button, 
  Box,
  IconButton,
  TablePagination,
  Divider
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import DeleteIcon from '@mui/icons-material/Delete';
import { getRetailerLogoForProduct } from "./shared/utils";
import Linechart from './shared/linechart';

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    padding: 0,
    width: '100%',
    margin: 0,
  },
  card: {
    width: '100%',
    height: 'auto',
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(2),
  },
  link: {
    textDecoration: 'none',
    color: theme.palette.primary.main,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  priceRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1, 0),
    position: 'relative',
    borderBottom: '1px solid rgba(0,0,0,0.05)',
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  priceLabel: {
    fontWeight: 'bold',
    color: theme.palette.text.secondary,
  },
  priceValue: {
    fontWeight: 'bold',
    color: theme.palette.text.primary,
    display: 'flex',
    alignItems: 'center',
  },
  chartContainer: {
    position: 'absolute',
    zIndex: 10,
    backgroundColor: 'white',
    boxShadow: theme.shadows[3],
    padding: theme.spacing(2),
    width: '100%',
    left: 0,
    top: '100%',
    borderRadius: '4px',
    border: '1px solid rgba(0,0,0,0.1)',
  },
  deleteButton: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    color: theme.palette.error.main,
    backgroundColor: 'rgba(255,255,255,0.8)',
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.9)',
    },
    zIndex: 1,
  },
  alertIcon: {
    width: 20,
    height: 20,
    marginRight: theme.spacing(1),
  },
  retailerSection: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  productSection: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: theme.spacing(2),
  },
  productImage: {
    width: '100%',
    height: 'auto',
    maxHeight: 150,
    objectFit: 'contain',
    marginBottom: theme.spacing(1),
  },
  productTitle: {
    marginBottom: theme.spacing(1),
    lineHeight: 1.3,
  },
  paginationContainer: {
    marginTop: theme.spacing(2),
  },
  enableButton: {
    marginTop: theme.spacing(2),
    width: '100%',
  },
  priceDrop: {
    color: theme.palette.success.main,
    fontWeight: 'bold',
  },
  lastUpdated: {
    fontSize: '0.85rem',
  },
  priceInfo: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}));

const SubscriptionTilesComponent = ({ 
  subscriptions, 
  onSubscriptionChange, 
  onDeleteSubscriptionRow,
  hoveredCell,
  handleMouseEnter,
  handleMouseLeave,
  page,
  rowsPerPage,
  handleChangePage,
  handleChangeRowsPerPage,
  count
}) => {
  const classes = useStyles();
  
  // Get subscriptions for current page
  const displayedSubscriptions = rowsPerPage > 0
    ? subscriptions.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : subscriptions;

  return (
    <div className={classes.root}>
      <Grid container spacing={0}>
        {displayedSubscriptions.map((subscription) => (
          <Grid item xs={12} sm={12} md={6} lg={4} key={`${subscription.catalogueId}-${subscription.prodId}`}>
            <Card className={classes.card}>
              <IconButton 
                className={classes.deleteButton} 
                aria-label="delete" 
                onClick={() => onDeleteSubscriptionRow(subscription)}
              >
                <DeleteIcon />
              </IconButton>
              
              <CardContent className={classes.content}>
                {/* Retailer logo */}
                <Box className={classes.retailerSection}>
                  {getRetailerLogoForProduct(subscription.retailerName)}
                </Box>
                
                {/* Product image and description */}
                <Box className={classes.productSection}>
                  <img 
                    src={subscription.prodImg} 
                    alt={subscription.prodDescription} 
                    className={classes.productImage} 
                  />
                  <Typography variant="subtitle1" className={classes.productTitle}>
                    <a 
                      href={subscription.productUrl} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className={classes.link}
                    >
                      {subscription.prodDescription}
                    </a>
                  </Typography>
                </Box>
                
                <Divider />
                
                {/* Price information in a list layout */}
                <Box className={classes.priceInfo}>
                  {/* Subscribed Price */}
                  <Box 
                    className={classes.priceRow}
                    onMouseEnter={() => handleMouseEnter(subscription, 'priceAtSubscription')}
                    onMouseLeave={handleMouseLeave}
                  >
                    <Typography className={classes.priceLabel}>Subscribed Price:</Typography>
                    <Typography className={classes.priceValue}>{subscription.priceAtSubscription}</Typography>
                    {hoveredCell.subscription === subscription && 
                     hoveredCell.column === 'priceAtSubscription' && 
                     subscription.priceHistory && (
                      <Box className={classes.chartContainer}>
                        <Linechart chartData={{
                          datasets: [
                            {
                              label: 'price',
                              data: subscription.priceHistory.map(({ price, creationDate }) => ({ x: creationDate.substring(0, 10), y: price })),
                              borderColor: 'rgb(53, 162, 235)',
                              backgroundColor: 'rgba(53, 162, 235, 0.5)',
                            },
                          ],
                        }} />
                      </Box>
                    )}
                  </Box>
                  
                  {/* Current Price */}
                  <Box 
                    className={classes.priceRow}
                    onMouseEnter={() => handleMouseEnter(subscription, 'currentPrice')}
                    onMouseLeave={handleMouseLeave}
                  >
                    <Typography className={classes.priceLabel}>Current Price:</Typography>
                    <Typography className={classes.priceValue}>
                      {subscription.currentPrice <= subscription.desiredPrice ? (
                        <span className={classes.priceDrop}>
                          <img className={classes.alertIcon} src='../../images/alert.png' title='Price drop alert' />
                          {subscription.currentPrice}
                        </span>
                      ) : (
                        <span>{subscription.currentPrice}</span>
                      )}
                    </Typography>
                    {hoveredCell.subscription === subscription && 
                     hoveredCell.column === 'currentPrice' && 
                     subscription.priceHistory && (
                      <Box className={classes.chartContainer}>
                        <Linechart chartData={{
                          datasets: [
                            {
                              label: 'price',
                              data: subscription.priceHistory.map(({ price, creationDate }) => ({ x: creationDate.substring(0, 10), y: price })),
                              borderColor: 'rgb(53, 162, 235)',
                              backgroundColor: 'rgba(53, 162, 235, 0.5)',
                            },
                          ],
                        }} />
                      </Box>
                    )}
                  </Box>
                  
                  {/* Desired Price */}
                  <Box 
                    className={classes.priceRow}
                    onMouseEnter={() => handleMouseEnter(subscription, 'desiredPrice')}
                    onMouseLeave={handleMouseLeave}
                  >
                    <Typography className={classes.priceLabel}>Desired Price:</Typography>
                    <Typography className={classes.priceValue}>{subscription.desiredPrice}</Typography>
                    {hoveredCell.subscription === subscription && 
                     hoveredCell.column === 'desiredPrice' && 
                     subscription.priceHistory && (
                      <Box className={classes.chartContainer}>
                        <Linechart chartData={{
                          datasets: [
                            {
                              label: 'price',
                              data: subscription.priceHistory.map(({ price, creationDate }) => ({ x: creationDate.substring(0, 10), y: price })),
                              borderColor: 'rgb(53, 162, 235)',
                              backgroundColor: 'rgba(53, 162, 235, 0.5)',
                            },
                          ],
                        }} />
                      </Box>
                    )}
                  </Box>
                  
                  {/* Last Updated */}
                  <Box 
                    className={classes.priceRow}
                    onMouseEnter={() => handleMouseEnter(subscription, 'lastFetchedTs')}
                    onMouseLeave={handleMouseLeave}
                  >
                    <Typography className={classes.priceLabel}>Last Updated:</Typography>
                    <Typography className={classes.priceValue}>
                      <span className={classes.lastUpdated}>{subscription.lastFetchedTs}</span>
                    </Typography>
                    {hoveredCell.subscription === subscription && 
                     hoveredCell.column === 'lastFetchedTs' && 
                     subscription.priceHistory && (
                      <Box className={classes.chartContainer}>
                        <Linechart chartData={{
                          datasets: [
                            {
                              label: 'price',
                              data: subscription.priceHistory.map(({ price, creationDate }) => ({ x: creationDate.substring(0, 10), y: price })),
                              borderColor: 'rgb(53, 162, 235)',
                              backgroundColor: 'rgba(53, 162, 235, 0.5)',
                            },
                          ],
                        }} />
                      </Box>
                    )}
                  </Box>
                </Box>
                
                {/* Action buttons */}
                <Button 
                  variant={subscription.enable ? "contained" : "outlined"} 
                  color="primary"
                  onClick={() => onSubscriptionChange(subscription)}
                  className={classes.enableButton}
                >
                  {subscription.enable ? "Enabled" : "Disabled"}
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      
      {/* Pagination controls */}
      <Box className={classes.paginationContainer}>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
          component="div"
          count={count || subscriptions.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Box>
    </div>
  );
};

export default SubscriptionTilesComponent;
